import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from '../hooks/useToast';
import { reportError } from '../utils/reportError';
import { ApiCallError } from '../utils/api';

const GENERIC_ERROR_MESSAGE = 'Došlo je do greške, pokušajte ponovo.';

const showGenericErrorToast = () =>
  toast.show({ type: 'error', message: GENERIC_ERROR_MESSAGE });

// API failures are already reported to Sentry with rich context (status / url /
// body) inside callPublicAPI, and surface here as ApiCallError (callPublicAPI
// throws one on failure for any caller that did not pass its own onError /
// fallback). Reporting them again here would duplicate the capture, so the
// global handlers only report errors that did NOT originate from the API layer
// — e.g. a genuine mapping bug or null-deref in code that never went through
// callPublicAPI.
const reportIfUnexpected = (
  error: Error,
  op: string,
  extras: Record<string, unknown>
): void => {
  if (error instanceof ApiCallError) {
    return;
  }
  reportError(error, { op, extras });
};

const QueryClientFactory = () =>
  new QueryClient({
    // Global fallback: any unhandled query/mutation failure still gives the
    // user a visible signal. Call sites with an explicit onError keep their
    // own (more specific) message — see mutationCache guard below.
    queryCache: new QueryCache({
      onError: (error, query) => {
        reportIfUnexpected(error, 'query', { queryKey: query.queryKey });
        if (query.meta?.skipGlobalErrorToast) {
          return; // the query's component renders its own error UI
        }
        showGenericErrorToast();
      }
    }),
    mutationCache: new MutationCache({
      onError: (error, _vars, _ctx, mutation) => {
        reportIfUnexpected(error, 'mutation', {
          mutationKey: mutation.options.mutationKey
        });
        if (
          mutation.options.onError ||
          mutation.options.meta?.skipGlobalErrorToast
        ) {
          return; // explicit handler wins, or the caller opted out
        }
        showGenericErrorToast();
      }
    }),
    defaultOptions: {
      queries: {
        // Don't refetch on focus for dev environments, having the latest data isn't that important
        refetchOnWindowFocus: !import.meta.env.DEV,
        retry: 1
      }
    }
  });

export const queryClient = QueryClientFactory();
